import { ChevronDownIcon, DotsHorizontalIcon, DownloadIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import { Link, useNavigate, useParams } from 'react-router';
import { CompetitionClassRegistrationsCollapsibleDatatable } from '../components/datatables/competitionClassRegistrationsCollapsibleDatatable';
import { CompetitionClassResultsDatatable } from '../components/datatables/competitionClassResultsDatatable';
import { HeightLengthDatatable } from '../components/datatables/heightLengthDatatable';
import { StraightCrookedDatatable } from '../components/datatables/straightCrookedDatatable';
import AddNewHeightLengthLevelDialog from '../components/dialogs/addNewHeightLengthLevelDialog';
import AddNewRoundDialog from '../components/dialogs/addNewRoundDialog';
import AddRegistrationDialog from '../components/dialogs/addRegistrationDialog';
import CheckCompetitionClassEligibilityDialog from '../components/dialogs/checkCompetitionClassEligibilityDialog';
import FinishClassDialog from '../components/dialogs/finishClassDialog';
import RearrangeStartNumbersDialog from '../components/dialogs/rearrangeStartNumbersDialog';
import ShuffleStartsDialog from '../components/dialogs/shuffleStartsDialog';
import SplitCompetitionSportClassDialog from '../components/dialogs/splitCompetitionSportClassDialog';
import TransferRegistrationsDialog from '../components/dialogs/transferRegistrationsDialog';
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from '../components/ui/badge';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from '../components/ui/button';
import {
    Dialog,
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import sport_classes from '../enums/sport_classes.json';
import api from '../lib/api';

// Human readable names
const columnNameMapping = {
    placing: "Placering",
    place: "Placering",
    startNumber: "Startnummer",
    bunnyName: "Kanin",
    driver_name: "Förare",
    comment: 'Kommentar',
    errors: "Fel",
    time: "Tid",
    formatted_attempts: "Försök",
    totalTime: "Total tid",
    totalFaults: "Antal fel",
    awards: "Utmärkelser",
    highestHeight: "Bäst resultat"
};

function ColumnVisibilityControl({ columns, columnVisibility, setColumnVisibility }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="">
                    Kolumner <ChevronDownIcon className="ml-2 h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dropdown-content-width-full">
                <DropdownMenuLabel>Kolumner</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => {
                    const columnId = column.id || column.accessorKey;
                    // Skip columns that shouldn't be hideable
                    if (column.enableHiding === false) return null;
                    const headerText = columnNameMapping[columnId] || columnId;
                    return (
                        <DropdownMenuCheckboxItem
                            key={columnId}
                            className="cursor-pointer"
                            checked={columnVisibility[columnId] !== false}
                            onCheckedChange={(value) =>
                                setColumnVisibility((prev) => ({
                                    ...prev,
                                    [columnId]: value,
                                }))
                            }
                        >
                            {headerText}
                        </DropdownMenuCheckboxItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const CompetitionClassSingle = () => {
    const navigate = useNavigate();
    const { competitionClassId } = useParams();
    const { auth } = useContext(AuthContext);
    const [competitionClass, setCompetitionClass] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [heightLengthLevels, setHeightLengthLevels] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [firstRoundAdded, setFirstRoundAdded] = useState(false);
    const [defaultTab, setDefaultTab] = useState(null);
    const [tabLoading, setTabLoading] = useState(false);
    const [childColumns, setChildColumns] = useState([]);

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('columnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {};
    });

    useEffect(() => {
        localStorage.setItem('columnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    const isHeightOrLength = (classId) => {
        const sportClass = sport_classes.find(c => c.id === classId);
        return sportClass && (sportClass.sport_class_name.includes("Höjd") || sportClass.sport_class_name.includes("Längd"));
    };

    const fetchClass = useCallback(async (withLoading = true) => {
        try {
            if (withLoading) {
                setIsLoading(true);
                setTabLoading(true);
            }
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/class/${competitionClassId}`);
            const classData = response.data;

            // Extract unique and sorted height/length levels
            const heightLevels = classData.registrations.flatMap((registration) =>
                registration.heightLengthResults?.map((result) => parseFloat(result.height_length)) || []
            ).filter(Boolean); // Remove invalid values
            const sortedHeightLevels = [...new Set(heightLevels)].sort((a, b) => a - b);

            // Extract unique rounds
            const roundNumbers = [];
            if (classData.max_errors_round2 !== null) roundNumbers.push(2);
            if (classData.max_errors_round3 !== null) roundNumbers.push(3);
            if (classData.registrations.some((r) => r.straightCrookedResults?.some((res) => res.round === 1))) {
                roundNumbers.unshift(1); // Ensure round 1 is always added first
            }

            // Set processed data
            setHeightLengthLevels(sortedHeightLevels.map((level) => level.toString())); // Convert to strings for Tabs
            setRounds(roundNumbers.map((round) => `round-${round}`)); // Convert rounds for Tabs
            setCompetitionClass(classData);

            // Determine default tab
            const latestHeight = sortedHeightLevels[sortedHeightLevels.length - 1]?.toString(); // Tallest height
            const latestRound = roundNumbers[roundNumbers.length - 1] ? `round-${roundNumbers[roundNumbers.length - 1]}` : null; // Latest round
            const initialTab = classData.finished_at
                ? 'results'
                : latestHeight
                    ? latestHeight // Go to tallest height/length level
                    : latestRound
                        ? latestRound
                        : 'registrations';
            setDefaultTab(initialTab);

            setError(null);
        } catch (error) {
            if (error.response?.status === 404) {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: 'Klassen du efterfrågade finns inte.',
                });
                navigate(`/upcoming`);
            }
            setCompetitionClass({});
        } finally {
            setIsLoading(false);
            setTabLoading(false);
        }
    }, [competitionClassId, navigate]); // Dependencies

    useEffect(() => {
        fetchClass();
    }, [fetchClass]);

    const formatDate = (date) => new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });

    // if (isLoading) return <div className='flex w-full min-h-64 items-center justify-center'><UpdateIcon className='loader-spinner size-12' /></div>;
    if (error) return <div className='flex w-full items-center justify-center'><div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'><h1 className='font-medium text-2xl'>Error</h1><p className='text-red-500'>{error}</p></div></div>;

    const getUniqueRounds = (competitionClass, firstRoundAdded) => {
        const roundsSet = new Set();

        if (isLoading) return;

        // Include Omgång 1 only if explicitly added or exists in data
        if (firstRoundAdded || competitionClass?.registrations?.some((r) =>
            r.straightCrookedResults?.some((res) => res.round === 1))) {
            roundsSet.add(1);
        }

        // Add rounds dynamically based on data
        if (competitionClass?.max_errors_round2 !== null) {
            roundsSet.add(2);
        }
        if (competitionClass?.max_errors_round3 !== null) {
            roundsSet.add(3);
        }
        return Array.from(roundsSet).sort((a, b) => a - b);
    };

    const uniqueRounds = getUniqueRounds(competitionClass, firstRoundAdded);

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <div className='grid grid-cols-1 lg:grid-cols-2 items-center'>
                    <div>
                        {isLoading ? (
                            <div className='flex flex-col gap-3'>
                                <Skeleton className='h-8 w-full' />
                                <Skeleton className='h-4 w-40' />
                            </div>
                        ) : (
                            <>
                                <h1 className='font-medium text-2xl flex items-center'>Klassdetaljer för {competitionClass?.name} {competitionClass?.assessment && ` - Bedömning ${competitionClass?.assessment}`}
                                    {(competitionClass?.canceled && <Badge variant='destructive' className='ml-2'>Inställd</Badge>) ||
                                        (competitionClass?.finished_at && <Badge variant='secondary' className='ml-2'>Avslutad</Badge>)}
                                </h1>

                                <Breadcrumb className='mt-2'>
                                    <BreadcrumbList>
                                        <BreadcrumbItem>
                                            {competitionClass?.competition?.finished_at ? <BreadcrumbLink asChild><Link to="/results">Resultat</Link></BreadcrumbLink> : <BreadcrumbLink asChild ><Link to="/upcoming">Kommande tävlingar</Link></BreadcrumbLink>}
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbLink asChild><Link to={`/competition/${competitionClass?.competition?.id}`}>{competitionClass?.competition?.name}</Link></BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbPage>{competitionClass?.name}</BreadcrumbPage>
                                        </BreadcrumbItem>
                                    </BreadcrumbList>
                                </Breadcrumb>
                            </>
                        )}
                    </div>
                    {(!isLoading && (auth?.user?.superadmin || auth?.user?.bunnyadmin || auth?.user?.judge || (auth?.user?.roleInClub === 'admin' && auth?.user?.memberOfClub === parseInt(competitionClass?.competition.club.id)))) &&
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 justify-end'>
                            <Dialog modal={true}>
                                <DropdownMenu >
                                    <DropdownMenuTrigger asChild className='md:col-start-2'>
                                        <Button variant='secondary'><DotsHorizontalIcon className='mr-3' />Verktyg</Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className='dropdown-content-width-full'>

                                        <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                                        {/* <ScrollArea className='h-fit dropdown-content-width-full'> */}
                                        <DropdownMenuSeparator />
                                        {!competitionClass?.finished_at && (
                                            <>
                                                <AddRegistrationDialog fetchClass={fetchClass} dropdown={true} competitionClassId={competitionClass?.id} cup={competitionClass?.cup} />
                                                <TransferRegistrationsDialog fetchClass={fetchClass} currentClass={competitionClass} dropdown={true} />
                                                <SplitCompetitionSportClassDialog competitionClass={competitionClass} />
                                                <RearrangeStartNumbersDialog data={competitionClass?.registrations} fetchClass={fetchClass} dropdown={true} />
                                                <ShuffleStartsDialog competitionClass={competitionClass} fetchClass={fetchClass} dropdown={true} />
                                                <CheckCompetitionClassEligibilityDialog competitionClass={competitionClass} fetchClass={fetchClass} dropdown={true} />
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem>
                                                    <CSVLink
                                                        filename={"participants_class_" + competitionClass?.id + ".csv"}
                                                        data={competitionClass?.registrations?.map(registration => ({
                                                            "Kanin": registration.bunny.name,
                                                            "Förare": registration.driver.first_name + " " + registration.driver.last_name,
                                                            "Utanför tävling": registration.outOfCompetition,
                                                            "Struket ekipage": registration.crossedOut,
                                                            "Startnummer": registration.startNumber,
                                                            "Eventuell kommentar": registration.comment
                                                        }))}
                                                        className="flex items-center w-full"
                                                    >
                                                        <DownloadIcon className='mr-3' />
                                                        Exportera anmälningslista
                                                    </CSVLink>
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        {competitionClass?.finished_at && (
                                            <DropdownMenuItem>
                                                <CSVLink
                                                    filename={"participants_class_" + competitionClass?.id + "_results.csv"}
                                                    data={competitionClass?.registrations?.map(registration => ({
                                                        "Placering": registration.placing,
                                                        "Kanin": registration.bunny.name,
                                                        "Förare": registration.driver.first_name + " " + registration.driver.last_name,
                                                        "Utmärkelser": registration.award ? [
                                                            registration.award.advancementPoint ? "Pinne" : "",
                                                            registration.award.certificate ? "Certifikat" : "",
                                                            registration.award.prize ? "Pris" : ""
                                                        ].filter(Boolean).join(" ") : "",
                                                        "Kommentar": registration.comment,
                                                    }))}
                                                    className="flex items-center w-full"
                                                >
                                                    <DownloadIcon className='mr-3' />
                                                    Exportera resultatlista
                                                </CSVLink>
                                            </DropdownMenuItem>
                                        )}
                                        {((!competitionClass?.finished_at && auth?.user?.judge) || auth?.user?.superadmin) && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <FinishClassDialog fetchClass={fetchClass} competitionClass={competitionClass} dropdown={true} />
                                            </>
                                        )}
                                        {/* </ScrollArea> */}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </Dialog>
                        </div>
                    }
                </div>

                {isLoading ? (
                    <div className='flex flex-col w-1/2 gap-2'>
                        <Skeleton className='h-4 w-2/3' />
                        <Skeleton className='h-4 w-5/6' />
                        <Skeleton className='h-4 w-3/4' />
                    </div>
                ) : (
                    <div >
                        {competitionClass?.split &&
                            <p><span className='font-medium'>Ursprunglig klass:</span> <Link className='text-blue-600 cursor-pointer hover:underline' to={`/competition/${competitionClass?.competition?.id}/class/${competitionClass?.split}`}>{competitionClass?.name.split('(Ny)')}</Link></p>
                        }
                        <p><span className='font-medium'>Datum:</span> {formatDate(competitionClass?.date)}</p>
                        {competitionClass?.cup?.name &&
                            <p><span className='font-medium'>Ingår i cup:</span> <Link className='text-blue-600 cursor-pointer hover:underline' to={`/competition/${competitionClass?.competition?.id}/cup/${competitionClass?.cup?.id}`}>{competitionClass?.cup?.name}</Link></p>
                        }
                        {competitionClass?.comment && <p><span className='font-medium'>Avslutande kommentar:</span> {competitionClass?.comment}</p>}
                        {competitionClass?.judge && <p><span className='font-medium'>Domare:</span> {competitionClass?.judge.first_name} {competitionClass?.judge.last_name}</p>}
                        {competitionClass?.finished_at && (
                            <>
                                <p><span className='font-medium'>Tidtagare 1:</span> {competitionClass?.time_keeper_1}</p>
                                <p><span className='font-medium'>Tidtagare 2:</span> {competitionClass?.time_keeper_2}</p>
                            </>
                        )}
                    </div>
                )}
                {competitionClass?.split && !competitionClass?.registrations?.length > 0 ? (
                    <Alert className='[&>svg]:text-skhrf-blue'>
                        <InfoCircledIcon />
                        <AlertTitle>Ny-klassen har inga registreringar</AlertTitle>
                        <AlertDescription>
                            Du kan flytta hit registreringar från den ursprungliga klassen från verktyg.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 items-center'>
                            <div>
                                <h2 className='font-medium text-xl mt-4'>Registreringar</h2>
                                {isLoading ?
                                    <Skeleton className='h-4 w-5/6 mt-1' />
                                    :
                                    competitionClass?.registrations?.length > 0 && (
                                        <p className='text-muted-foreground text-sm'>
                                            {competitionClass?.registrations?.filter(registration => !registration.crossedOut).length} ekipage varav {competitionClass?.registrations?.filter(registration => registration.outOfCompetition && !registration.crossedOut).length} är utom tävlan.
                                        </p>
                                    )
                                }
                            </div>
                        </div>

                        <Tabs
                            key={defaultTab}
                            defaultValue={defaultTab}
                            onValueChange={(value) => {
                                if (value.startsWith('round-') && !tabLoading) {
                                    setTabLoading(true); // Prevent switching prematurely
                                    setTimeout(() => setTabLoading(false), 300); // Simulate data/component load delay
                                }
                            }}
                            className='flex flex-col mt-4'
                        >
                            <div className='flex flex-col md:flex-row justify-between gap-3'>
                                <TabsList className='flex flex-wrap justify-start h-fit w-fit'>
                                    {isLoading ? (
                                        <Skeleton className='h-4 w-96' />
                                    ) : (
                                        <>
                                            <TabsTrigger value="registrations">Anmälda ekipage</TabsTrigger>
                                            {heightLengthLevels?.map((level, index) => (
                                                <TabsTrigger key={index} value={level}>{parseInt(level)} cm</TabsTrigger>
                                            ))}
                                            {/* {uniqueRounds?.map((round) => (
                                                <TabsTrigger key={round} value={`round-${round}`}>
                                                    {`Omgång ${round}, fel`}
                                                </TabsTrigger>
                                            ))} */}
                                            {uniqueRounds?.map((round) => (
                                                <TabsTrigger key={round} value={`round-${round}`}>
                                                    {`Omgång ${round}${round > 1
                                                            ? ` (${round === 2
                                                                ? competitionClass?.max_errors_round2
                                                                : competitionClass?.max_errors_round3
                                                            } fel)`
                                                            : ""
                                                        }`}
                                                </TabsTrigger>
                                            ))}
                                            {isHeightOrLength(competitionClass?.sport_class_id) && (!competitionClass?.finished_at) && (competitionClass?.registrations?.length > 0) && (
                                                <TabsTrigger asChild>
                                                    <AddNewHeightLengthLevelDialog
                                                        competitionClass={{
                                                            ...competitionClass,
                                                            heightLengthLevels,
                                                        }}
                                                        onUpdateClass={(updatedClass) => {
                                                            setCompetitionClass(updatedClass);

                                                            // Update height/length levels
                                                            const updatedLevels = updatedClass.heightLengthLevels;
                                                            setHeightLengthLevels(updatedLevels);

                                                            // Switch to the latest added height/length level
                                                            const latestLevel = updatedLevels[updatedLevels.length - 1];
                                                            if (latestLevel) {
                                                                setDefaultTab(latestLevel.toString());
                                                            }
                                                        }}
                                                    />
                                                </TabsTrigger>
                                            )}
                                            {(auth?.user?.superadmin || auth?.user?.judge) &&
                                                !isHeightOrLength(competitionClass?.sport_class_id) &&
                                                !competitionClass?.finished_at &&
                                                (rounds.length === 0 || rounds.length < 3) && (competitionClass?.registrations?.length > 0) && ( // Show dialog if no rounds exist or fewer than 3 rounds
                                                    <TabsTrigger asChild>
                                                        <AddNewRoundDialog
                                                            competitionClass={competitionClass}
                                                            onUpdateClass={(updatedClass) => {
                                                                setCompetitionClass(updatedClass);
                                                                const updatedRounds = getUniqueRounds(updatedClass, firstRoundAdded);
                                                                setRounds(updatedRounds); // Update rounds state
                                                                if (!firstRoundAdded && updatedClass?.max_errors_round2 === null) {
                                                                    setFirstRoundAdded(true);
                                                                }

                                                                // Set default tab to the latest round
                                                                const latestRound = updatedRounds[updatedRounds.length - 1];
                                                                if (latestRound) {
                                                                    setDefaultTab(`round-${latestRound}`);
                                                                }
                                                            }}
                                                        />
                                                    </TabsTrigger>
                                                )
                                            }
                                            {competitionClass?.finished_at &&
                                                <TabsTrigger value='results'>Resultat</TabsTrigger>
                                            }
                                        </>
                                    )}
                                </TabsList>
                                <ColumnVisibilityControl
                                    columns={childColumns}
                                    columnVisibility={columnVisibility}
                                    setColumnVisibility={setColumnVisibility}
                                />
                            </div>
                            <TabsContent value="registrations">
                                <CompetitionClassRegistrationsCollapsibleDatatable competitionClass={competitionClass} isLoading={isLoading} fetchClass={fetchClass} forCup={false} columnVisibility={columnVisibility} setColumnVisibility={setColumnVisibility} onColumnsReady={setChildColumns} />
                            </TabsContent>
                            {heightLengthLevels?.map((level, index) => (
                                <TabsContent value={level} key={index}>
                                    <HeightLengthDatatable
                                        competitionClass={competitionClass}
                                        level={parseInt(level.match(/\d+/)?.[0] || 0, 10)} // Extract numeric part
                                        // level={level}
                                        isLoading={isLoading}
                                        fetchClass={fetchClass}
                                        columnVisibility={columnVisibility}
                                        setColumnVisibility={setColumnVisibility}
                                        onColumnsReady={setChildColumns}
                                    />
                                </TabsContent>
                            ))}
                            {uniqueRounds?.map((round) => (
                                <TabsContent value={`round-${round}`} key={round}>
                                    <StraightCrookedDatatable
                                        competitionClass={competitionClass}
                                        level={round}
                                        fetchClass={fetchClass}
                                        isLoading={isLoading}
                                        columnVisibility={columnVisibility}
                                        setColumnVisibility={setColumnVisibility}
                                        onColumnsReady={setChildColumns}
                                    />
                                </TabsContent>
                            ))}
                            <TabsContent value='results'>
                                <CompetitionClassResultsDatatable
                                    competitionClass={competitionClass}
                                    fetchClass={fetchClass}
                                    isLoading={isLoading}
                                    columnVisibility={columnVisibility}
                                    setColumnVisibility={setColumnVisibility}
                                    onColumnsReady={setChildColumns}
                                />
                            </TabsContent>
                        </Tabs>
                    </>
                )
                }
            </div>
        </div>
    );
};

export default CompetitionClassSingle;
